import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Typography,
} from '@mui/material';
import { usePostHog } from '@posthog/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker, useLocation } from 'react-router-dom';
import { capture } from '../../analytics/posthog';
import ApiClient from '../../config/ApiClient';
import { useAppContext } from '../../context/AppContext';
import { useLessonPlanRecording } from '../../context/LessonPlanRecordingContext';
import { LessonPlanModel, LessonPlanSessionModel, SessionSlideModel } from '../../object-actions/types/types';
import { normalizeEntitlementsMap } from '../../subscriptions/entitlements';
import { DEFAULT_PLAN_ID, getOfferedPlan } from '../../subscriptions/purchaseSteps';
import type { MembershipState } from '../../subscriptions/types';
import SubscriptionPurchaseCta from '../SubscriptionPurchaseCta';
import { ProgramWeek } from '../growth-report/GrowthReportEnqueueButton';
import LessonPlanCard from './LessonPlanCard';
import LessonPlanSessionNav from './LessonPlanSessionNav';
import { getFirstIncompleteSessionIndex, getFirstUnstartedSlideIndex } from './lessonPlanProgressUtils';

export type LessonPlanSelectSession = (
    sessionId: number,
    source: 'dropdown' | 'prev_next' | 'session_nav',
) => void;

interface LessonPlanRendererProps {
    entity: LessonPlanModel;
    /** Lets the page header session picker reuse Renderer selection + recording guards. */
    onSelectSessionReady?: (selectSession: LessonPlanSelectSession) => void;
}

const SURVEY_WEEKS: ProgramWeek[] = [4, 8, 12];

type SurveyListResponse =
    | { results?: Array<{ session_number?: number; lesson_plan?: number | { id?: number } }> }
    | Array<{ session_number?: number; lesson_plan?: number | { id?: number } }>;

const LessonPlanRenderer: React.FC<LessonPlanRendererProps> = ({ entity, onSelectSessionReady }) => {
    if (!entity?.sessions || entity.sessions.length === 0) {
        return (
            <Box>
                <Typography variant="body1" color="text.secondary">
                    No lesson plan content available.
                </Typography>
            </Box>
        );
    }

    return <LessonPlanRendererContent entity={entity} onSelectSessionReady={onSelectSessionReady} />;
};

interface LessonPlanRendererContentProps {
    entity: LessonPlanModel;
    onSelectSessionReady?: (selectSession: LessonPlanSelectSession) => void;
}

const LessonPlanRendererContent: React.FC<LessonPlanRendererContentProps> = ({
    entity,
    onSelectSessionReady,
}) => {
    const posthog = usePostHog();
    const location = useLocation();
    const { organization } = useAppContext();
    const {
        activeSessionId,
        setActiveSessionId,
        setActiveSlideId,
        stopRecording,
        isStarted,
        isRecording,
        uploadQueue,
        uploading,
        getCurrentRecording,
        waitForUploadQueueIdle,
        abandonRecordingAndUploads
    } = useLessonPlanRecording();
    const [growthSnack, setGrowthSnack] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({ open: false, message: '', severity: 'success' });
    const [showChangeLessonDialog, setShowChangeLessonDialog] = useState(false);
    const [pendingSessionId, setPendingSessionId] = useState<number | null>(null);
    const [navLeaveBusy, setNavLeaveBusy] = useState(false);
    const [completedSurveyWeeks, setCompletedSurveyWeeks] = useState<Partial<Record<ProgramWeek, boolean>>>({});
    const [highlightSurveyWeek, setHighlightSurveyWeek] = useState<ProgramWeek | null>(null);
    const [upsellDialogOpen, setUpsellDialogOpen] = useState(false);
    const recordingGuardRef = useRef({
        isRecording,
        uploadQueueLength: uploadQueue.length,
        uploading
    });
    recordingGuardRef.current = {
        isRecording,
        uploadQueueLength: uploadQueue.length,
        uploading
    };

    const shouldBlockRouteChange = useCallback(
        () =>
            recordingGuardRef.current.isRecording() ||
            recordingGuardRef.current.uploadQueueLength > 0 ||
            recordingGuardRef.current.uploading,
        [isRecording, uploadQueue.length, uploading]
    );

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (currentLocation.pathname === nextLocation.pathname) {
            return false;
        }
        return shouldBlockRouteChange();
    });

    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            const g = recordingGuardRef.current;
            if (g.isRecording() || g.uploadQueueLength > 0 || g.uploading) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    const handleNavigationStopAndUpload = async () => {
        if (blocker.state !== 'blocked') return;
        setNavLeaveBusy(true);
        try {
            if (getCurrentRecording()) {
                await stopRecording(true, false);
            }
            await waitForUploadQueueIdle();
            blocker.proceed();
        } catch (err) {
            console.error('Stop and upload before navigation failed:', err);
        } finally {
            setNavLeaveBusy(false);
        }
    };

    const handleNavigationLeaveAnyway = async () => {
        if (blocker.state !== 'blocked') return;
        setNavLeaveBusy(true);
        try {
            await abandonRecordingAndUploads();
            blocker.proceed();
        } catch (err) {
            console.error('Abandon recording before navigation failed:', err);
        } finally {
            setNavLeaveBusy(false);
        }
    };

    const handleNavigationStay = () => {
        if (blocker.state === 'blocked') {
            blocker.reset();
        }
    };

    const sessions = entity.sessions || [];
    const myRoles = entity.my_roles;
    const isObserver =
        myRoles?.includes('coach') || myRoles?.includes('parent') || myRoles?.includes('payer');

    const lessonPlanId: number | null = entity?.id
        ? (typeof entity.id === 'number' ? entity.id : Number(entity.id))
        : null;

    // Load this viewer's progress-survey completion for weeks 4/8/12 (coach / parent / payer).
    useEffect(() => {
        if (!isObserver || lessonPlanId == null) {
            setCompletedSurveyWeeks({});
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await ApiClient.get<SurveyListResponse>(
                    `/api/survey-responses/?lesson_plan=${lessonPlanId}`,
                );
                if (cancelled || !response.success || !response.data) return;
                const rows = Array.isArray(response.data)
                    ? response.data
                    : (response.data.results ?? []);
                const next: Partial<Record<ProgramWeek, boolean>> = {};
                for (const row of rows) {
                    const week = row.session_number;
                    const rowLessonPlanId =
                        typeof row.lesson_plan === 'number'
                            ? row.lesson_plan
                            : row.lesson_plan?.id;
                    if (rowLessonPlanId != null && rowLessonPlanId !== lessonPlanId) {
                        continue;
                    }
                    if (week === 4 || week === 8 || week === 12) {
                        next[week] = true;
                    }
                }
                setCompletedSurveyWeeks(next);
            } catch (err) {
                console.error('Error loading progress survey status:', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isObserver, lessonPlanId]);

    // Open the first incomplete session when the lesson plan loads or reloads.
    useEffect(() => {
        if (!sessions.length) return;
        const idx = getFirstIncompleteSessionIndex(sessions);
        const ses = sessions[idx];
        const j = getFirstUnstartedSlideIndex(ses) ?? 0;
        const sl = (ses?.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (ses?.slides || [])[0];
        const lpId = entity?.id;
        if (ses?.id != null && posthog && lpId) {
            capture(posthog, "lesson_session_viewed", {
                lesson_plan_id: lpId,
                session_id: ses.id,
                session_number: ses.session_number ?? ses.id,
                organization_id: organization?.id,
                source: 'session_nav'
            });
        }
        setActiveSessionId(ses?.id ?? null);
        setActiveSlideId(sl?.id ?? null);
        setHighlightSurveyWeek(null);
    }, [entity, setActiveSessionId, setActiveSlideId]);

    const fireSessionViewed = useCallback((
        session: LessonPlanSessionModel,
        source: 'dropdown' | 'prev_next' | 'session_nav',
    ) => {
        if (!posthog || !session?.id || !lessonPlanId) return;
        capture(posthog, "lesson_session_viewed", {
            lesson_plan_id: lessonPlanId,
            session_id: session.id,
            session_number: session.session_number ?? session.id,
            organization_id: organization?.id,
            source
        });
    }, [posthog, lessonPlanId, organization?.id]);

    const selectSession = useCallback((
        sessionId: number,
        source: 'dropdown' | 'prev_next' | 'session_nav',
    ) => {
        if (isStarted && activeSessionId != null && sessionId !== activeSessionId) {
            setPendingSessionId(sessionId);
            setShowChangeLessonDialog(true);
            return;
        }
        // Re-clicking the current session tab must not jump to the first unstarted
        // slide: that skip is not tracked (deck already mounted) and Mark Complete
        // stays hidden until every slide has started_at.
        if (sessionId === activeSessionId) {
            setHighlightSurveyWeek(null);
            return;
        }
        const ses = sessions.find((s) => s.id === sessionId);
        if (!ses) return;
        fireSessionViewed(ses, source);
        setActiveSessionId(ses.id);
        const j = getFirstUnstartedSlideIndex(ses) ?? 0;
        const sl = (ses.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (ses.slides || [])[0];
        setActiveSlideId(sl?.id ?? null);
        setHighlightSurveyWeek(null);
    }, [
        isStarted,
        activeSessionId,
        sessions,
        fireSessionViewed,
        setActiveSessionId,
        setActiveSlideId,
    ]);

    useEffect(() => {
        onSelectSessionReady?.(selectSession);
    }, [onSelectSessionReady, selectSession]);

    const selectSurveyTab = (week: ProgramWeek) => {
        const session = sessions.find(candidate => candidate.session_number === week);
        if (session?.id != null) {
            selectSession(session.id, 'session_nav');
            setHighlightSurveyWeek(week);
            // Scroll to session milestone CTAs after the session body mounts.
            window.setTimeout(() => {
                document
                    .querySelector(`[data-testid="lesson-plan-session-milestone--week-${week}"]`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 50);
        }
    };

    const handleDialogStopAndUpload = async () => {
        setShowChangeLessonDialog(false);
        await stopRecording(true, false).catch((e) => console.error('Stop & upload:', e));
        if (pendingSessionId != null) {
            const ses = sessions.find((s) => s.id === pendingSessionId);
            if (ses) {
                fireSessionViewed(ses, 'session_nav');
                const j = getFirstUnstartedSlideIndex(ses) ?? 0;
                const sl = (ses.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (ses.slides || [])[0];
                setActiveSessionId(ses.id);
                setActiveSlideId(sl?.id ?? null);
            }
        }
        setPendingSessionId(null);
    };

    const handleDialogDiscard = async () => {
        setShowChangeLessonDialog(false);
        await stopRecording(true, true).catch((e) => console.error('Discard:', e));
        if (pendingSessionId != null) {
            const ses = sessions.find((s) => s.id === pendingSessionId);
            if (ses) {
                fireSessionViewed(ses, 'session_nav');
                const j = getFirstUnstartedSlideIndex(ses) ?? 0;
                const sl = (ses.slides || []).filter((s): s is SessionSlideModel => s != null)[j] ?? (ses.slides || [])[0];
                setActiveSessionId(ses.id);
                setActiveSlideId(sl?.id ?? null);
            }
        }
        setPendingSessionId(null);
    };

    const handleDialogCancel = () => {
        setShowChangeLessonDialog(false);
        setPendingSessionId(null);
    };

    const rawPaymentAssignmentId = entity.assignment?.id;
    const paymentAssignmentId =
        rawPaymentAssignmentId != null && !Number.isNaN(Number(rawPaymentAssignmentId))
            ? Number(rawPaymentAssignmentId)
            : null;

    const upsellPlanId =
        getOfferedPlan(
            normalizeEntitlementsMap(
                (entity.assignment as { entity?: { entitlements?: unknown } } | undefined)?.entity
                    ?.entitlements,
            ),
        ) ?? DEFAULT_PLAN_ID;

    const assignmentMembership =
        (entity.assignment as { entity?: { membership?: MembershipState | null } } | undefined)
            ?.entity?.membership ?? null;

    const activeSession = sessions.find((s) => s.id === activeSessionId) ?? sessions[0];
    const handleGrowthMessage = (message: string, severity: 'success' | 'error') => {
        setGrowthSnack({ open: true, message, severity });
    };

    return (
        <Box data-testid="lesson-plan-renderer">
            <LessonPlanSessionNav
                sessions={sessions}
                activeSessionId={activeSession?.id ?? activeSessionId ?? 0}
                onSelectSession={selectSession}
                showSurveyTabs={Boolean(isObserver && lessonPlanId != null)}
                completedSurveyWeeks={completedSurveyWeeks}
                activeSurveyWeek={
                    highlightSurveyWeek &&
                        activeSession &&
                        SURVEY_WEEKS.includes(activeSession.session_number as ProgramWeek) &&
                        highlightSurveyWeek === activeSession.session_number
                        ? highlightSurveyWeek
                        : null
                }
                onSelectSurvey={selectSurveyTab}
                onUpsellSession={() => setUpsellDialogOpen(true)}
            />

            {activeSession && (
            <LessonPlanCard
                session={activeSession}
                lessonPlanId={lessonPlanId}
                sessions={sessions}
                paymentAssignmentId={paymentAssignmentId}
                onGrowthMessage={handleGrowthMessage}
                myRoles={myRoles}
                isPublished={!!entity.coach_delivered}
                hasHumanCoach={Boolean(entity.has_human_coach)}
                hideSessionTitle
            />
            )}

            <Dialog
                open={upsellDialogOpen}
                onClose={() => setUpsellDialogOpen(false)}
                aria-labelledby="lesson-plan-upsell-title"
                data-testid="lesson-plan-upsell-dialog"
            >
                <DialogTitle id="lesson-plan-upsell-title">Continue your program</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        You are still on your free trial. All sessions and growth reports unlock when your
                        membership begins.
                    </DialogContentText>
                    {paymentAssignmentId != null && (
                        <SubscriptionPurchaseCta
                            assignmentId={paymentAssignmentId}
                            productId={upsellPlanId}
                            returnTo={location.pathname}
                            compact
                            membership={assignmentMembership}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setUpsellDialogOpen(false)}
                        color="inherit"
                        data-attr="lesson-plan-renderer_upsell-close"
                    >
                        Not now
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showChangeLessonDialog}
                onClose={handleDialogCancel}
                aria-labelledby="change-lesson-dialog-title"
                aria-describedby="change-lesson-dialog-description"
            >
                <DialogTitle id="change-lesson-dialog-title">Recording in Progress</DialogTitle>
                <DialogContent>
                    <DialogContentText id="change-lesson-dialog-description">
                        You are currently recording. What would you like to do with the current recording?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogCancel} color="inherit" data-attr="lesson-plan-renderer_change-lesson-dialog-cancel">Cancel</Button>
                    <Button onClick={handleDialogDiscard} color="error" data-attr="lesson-plan-renderer_change-lesson-dialog-discard">Discard Recording</Button>
                    <Button onClick={handleDialogStopAndUpload} color="primary" variant="contained" data-attr="lesson-plan-renderer_change-lesson-dialog-stop-upload">Stop & Upload Recording</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={growthSnack.open}
                onClose={() => setGrowthSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                data-testid="growth-report-enqueue-snackbar"
            >
                <Alert
                    onClose={() => setGrowthSnack((s) => ({ ...s, open: false }))}
                    severity={growthSnack.severity}
                    sx={{ width: '100%' }}
                    data-testid="growth-report-enqueue-alert"
                >
                    {growthSnack.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={blocker.state === 'blocked'}
                onClose={navLeaveBusy ? undefined : handleNavigationStay}
                disableEscapeKeyDown={navLeaveBusy}
                aria-labelledby="recording-nav-guard-title"
                aria-describedby="recording-nav-guard-description"
            >
                <DialogTitle id="recording-nav-guard-title">Recording in progress</DialogTitle>
                <DialogContent>
                    <DialogContentText id="recording-nav-guard-description">
                        You have a lesson recording in progress or uploads pending. You can stop and save to the server, stay on this page, or leave and lose any unsaved audio.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleNavigationStay} color="inherit" disabled={navLeaveBusy} data-attr="lesson-plan-renderer_nav-guard-stay">
                        Stay on page
                    </Button>
                    <Button onClick={handleNavigationLeaveAnyway} color="error" disabled={navLeaveBusy} data-attr="lesson-plan-renderer_nav-guard-leave">
                        Leave without saving
                    </Button>
                    <Button
                        onClick={handleNavigationStopAndUpload}
                        color="primary"
                        variant="contained"
                        disabled={navLeaveBusy}
                        data-attr="lesson-plan-renderer_nav-guard-stop-upload"
                    >
                        {navLeaveBusy ? 'Saving…' : 'Stop and upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LessonPlanRenderer;
